import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Theme } from "../../theme/Theme";

const CommonModel = ({
  isOpen,
  title,
  onClose,
  children,
  draggable = true,
  closeOnBackdrop = true,
  closeOnEsc = true,
  width = "min(680px, 92vw)",
  height = "85vh",
  needCrossIcon = true,
  bodyStyle = {},
  bodyClassName = "p-3",
}) => {
  const modalRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const startRef = useRef({ x: 0, y: 0 });

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    if (!isOpen) setPos({ x: 0, y: 0 });    
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handleEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  // Dragging logic
  const onPointerDown = (e) => {
    if (!draggable) return;
    setDragging(true);
    startRef.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    setPos({
      x: e.clientX - startRef.current.x,
      y: e.clientY - startRef.current.y,
    });
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };
  // const handleBackdropMouseDown = (e) => {
  //   if (!closeOnBackdrop) return;
  //   if (e.target === e.currentTarget) onClose?.();
  // };

  if (!isOpen) return null;

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--overlay-backdrop)",
        zIndex: 1100,
      }}
      // onMouseDown={handleBackdropMouseDown} 
    >
      <div
        ref={modalRef}
        className="shadow-lg"
        style={{
          position: dragging ? "absolute" : "relative",
          transform: `translate(${pos.x}px, ${pos.y}px)`,
          background: "var(--bg-surface)",
          color: "var(--text-dark)",
          width,
          height,
          maxHeight: "85vh",
          borderRadius: 16,
          overflow: "hidden",
          zIndex: 1110,
          cursor: dragging ? "grabbing" : "default",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Header */}
        {(title || needCrossIcon) && <div
          className="d-flex align-items-center justify-content-between px-3 py-2"
          style={{ 
            cursor: draggable ? "grab" : "default", 
            userSelect: "none",
            borderBottom: "1px solid var(--border-subtle)" 
          }}
          onPointerDown={onPointerDown}
        >
          {title ? (
            <h5 className="mb-0 fw-bold" style={{ ...Theme.font20SemiBold, color: "var(--text-primary)" }}>{title}</h5>
          ) : (
            <div />
          )}
          {needCrossIcon && <button
            type="button"
            className="d-flex align-items-center justify-content-center rounded-circle"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}  // ✅
            onPointerDown={(e) => e.stopPropagation()}  
            style={{ 
              width: 32, 
              height: 32, 
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-surface-2)",
              color: "var(--text-primary)",
              cursor: "pointer"
            }}
          >
            <Icon icon="mdi:close" width={20} height={20} />
          </button>}
        </div>}
        {/* Body */}
        <div className={bodyClassName} style={{ flex: 1, overflow: "auto", ...bodyStyle }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CommonModel;
